import React, { useState } from 'react';
import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { Link, } from 'react-router-dom';
import Loader from './../components/Loader';
import Message from './../components/Message';

import {
  useGetExpensesQuery,
  useDeleteExpenseMutation,
  useCreateExpenseMutation,
} from './../slices/expensesApiSlice';
import { toast } from 'react-toastify';

const ExpenseUserList = () => {
  
  const [pageNumber, setPageNumber] = useState(1);
  const { data, isLoading, error, refetch } = useGetExpensesQuery({ pageNumber });
  const [deleteExpense, { isLoading: loadingDelete }] = useDeleteExpenseMutation();
  const [createExpense, { isLoading: loadingCreate }] = useCreateExpenseMutation();

  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Create a new expense
  const createExpenseHandler = async () => {
    if (window.confirm('Are you sure you want to create a new expense?')) {
      try {
        const newExpense = {
          date: new Date().toISOString().split('T')[0],
          account: 'Debit',
          accountName: 'Default Account',
          subAccount: 'Default Sub Account',
          details: 'New Expense',
          amount: 0,
          status: 'Pending',
          category: 'Other',
          notes: 'Default note',
        
        };
        await createExpense(newExpense).unwrap();
        toast.success('New expense created successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Delete an expense
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await deleteExpense(id).unwrap();
        toast.success('Expense deleted successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
  };

  // Filter and sort expenses
  const filterAndSortExpenses = () => {
    let filteredExpenses = [...(data?.expenses || [])];

    // Filter by text input
    if (filter) {
      filteredExpenses = filteredExpenses.filter(
        (expense) =>
          expense.account?.toLowerCase().includes(filter.toLowerCase()) ||
          expense.accountName?.toLowerCase().includes(filter.toLowerCase()) ||
          expense.details?.toLowerCase().includes(filter.toLowerCase()) ||
          expense.category?.toLowerCase().includes(filter.toLowerCase()) ||
          expense.subAccount?.toLowerCase().includes(filter.toLowerCase()) ||
          expense.notes?.toLowerCase().includes(filter.toLowerCase()) ||
          expense.username?.toLowerCase().includes(filter.toLowerCase())
      );
    }

    // Filter by date range
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filteredExpenses = filteredExpenses.filter((expense) => {
        const expenseDate = new Date(expense.date);
        return expenseDate >= start && expenseDate <= end;
      });
    }

    // Sort expenses
    if (sortField) {
      filteredExpenses.sort((a, b) => {
        if (sortField === 'amount') {
          return sortOrder === 'asc' ? a.amount - b.amount : b.amount - a.amount;
        } else if (sortField === 'date') {
          return sortOrder === 'asc'
            ? new Date(a.date) - new Date(b.date)
            : new Date(b.date) - new Date(a.date);
        } else {
          const aValue = a[sortField]?.toLowerCase() || '';
          const bValue = b[sortField]?.toLowerCase() || '';
          return sortOrder === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
      });
    }

    return filteredExpenses;
  };

  // Calculate total for date range
  const calculateTotalForDateRange = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);

    const filteredExpenses = data?.expenses?.filter((expense) => {
      const expenseDate = new Date(expense.date);
      return expenseDate >= start && expenseDate <= end;
    }) || [];

    return filteredExpenses.reduce((acc, expense) => acc + expense.amount, 0);
  };

  // Calculate total for all expenses
  const totalAmount = data?.expenses?.reduce((acc, expense) => acc + expense.amount, 0) || 0;
   // Handle pagination changes
   const paginationHandler = (page) => {
    setPageNumber(page);
  };

  const filteredTotalAmount = filterAndSortExpenses().reduce(
  (acc, expense) => acc + expense.amount,
  0
   );

  
  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h1>Income and Expense</h1>
        </Col>
        <Col className="text-end">
          <Button onClick={createExpenseHandler} className="my-3" style={{display:"none"}}>
            <FaPlus /> Add New Expense
          </Button>
          <Link to='/create/staff/expense' className='btn btn-light my-3'>
            Add Data
          </Link>
          <Link to='/create/expense/excelform' className='btn btn-light my-3'>
            AddFromExcel
          </Link>     
        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col>
          <h5>Total Amount: ${totalAmount.toFixed(2)}</h5>
        </Col>
        
  <Col>
    <h4>
      Total (Filtered): ${filteredTotalAmount.toFixed(2)}{' '}
      <span className="text-muted">({data?.expenses?.length} total)</span>
    </h4>
  </Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by account, name, details, category, subAccount, or notes"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start Date"
          />
        </Col>
        <Col md={4}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End Date"
          />
        </Col>
        <Col md={4}>
          <h5>
            Total (Filtered Range): ${calculateTotalForDateRange().toFixed(2)}
          </h5>
        </Col>
      </Row>
  
      {loadingCreate && <Loader />}
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'An error occurred'}</Message>
      ) : data?.expenses?.length > 0 ? (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                  Date {sortField === 'date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>Account</th>
                <th onClick={() => handleSort('accountName')} style={{ cursor: 'pointer' }}>
                  Account Name {sortField === 'accountName' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('details')} style={{ cursor: 'pointer' }}>
                  Details {sortField === 'details' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('amount')} style={{ cursor: 'pointer' }}>
                  Amount {sortField === 'amount' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>
                  Category {sortField === 'category' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('notes')} style={{ cursor: 'pointer' }}>
                  Notes {sortField === 'notes' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                
                <th>Images</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filterAndSortExpenses().map((expense) => (
                <tr key={expense._id}>
                  <td>{new Date(expense.date).toLocaleDateString()}</td>
                  <td>{expense.account}</td>
                  <td>{expense.accountName}</td>
                  <td>{expense.details}</td>
                  <td>
                    {expense.amount >= 0
                      ? `$${expense.amount.toFixed(2)}`
                      : `-$${Math.abs(expense.amount).toFixed(2)}`}
                  </td>
                  <td>{expense.category}</td>
                  <td>{expense.notes}</td>
                  
                  <td className="thumbnails">
                    {expense.images?.length > 0 && (
                      <img
                        src={expense.images[0]}
                        alt="Thumbnail"
                        className="thumbnail-img me-2"
                        style={{
                          width: '15px',
                          height: '15px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </td>

                  <td>
                    <Link
                       className="btn btn-primary btn-sm m-2"
                      to={`/expense/${expense._id}`}
                        role="button"
                    >
                    Details
                   </Link>
                    <Button
                      style={{display: "none"}}
                      as={Link}
                      to={`/expense/${expense._id}/edit`}
                      variant="light"
                      className="btn-sm mx-2"
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      style={{display: "none"}}
                      variant="danger"
                      className="btn-sm"
                      onClick={() => deleteHandler(expense._id)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pagination>
                       {[...Array(data.pages).keys()].map((x) => (
                          <Pagination.Item
                          key={x + 1}
                          active={x + 1 === pageNumber}
                          onClick={() => paginationHandler(x + 1)}
                         >
                        {x + 1}
                        </Pagination.Item>
                        ))}
          </Pagination>
                    
          
        </>
      ) : (
        <Message variant="info">No expenses found</Message>
      )}
    </>
  );
};

export default ExpenseUserList;